import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Skeleton } from '../components/ui/skeleton';
import { Building2, Plus, Trash2, MapPin, Calendar, Package, Loader2, TrendingUp, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DepotManagement = () => {
  const { getAuthHeaders } = useAuth();
  const [depots, setDepots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clearingStock, setClearingStock] = useState(null);
  const [clearingSales, setClearingSales] = useState(null);
  const [clearingTotals, setClearingTotals] = useState(false);
  const [newDepot, setNewDepot] = useState({ name: '', location: '' });
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    fetchDepots();
  }, []);

  const fetchDepots = async () => {
    try {
      const response = await axios.get(`${API}/depots`, { headers: getAuthHeaders() });
      setDepots(response.data);
    } catch (error) {
      console.error('Error fetching depots:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDepot = async () => {
    if (!newDepot.name || !newDepot.location) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      await axios.post(`${API}/depots`, newDepot, { headers: getAuthHeaders() });
      toast.success('Depot created successfully');
      setNewDepot({ name: '', location: '' });
      setDialogOpen(false);
      fetchDepots();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create depot');
    }
  };

  const handleDeleteDepot = async (depotId, depotName) => {
    try {
      await axios.delete(`${API}/depots/${depotId}`, { headers: getAuthHeaders() });
      toast.success(`${depotName} depot deleted`);
      fetchDepots();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete depot');
    }
  };

  const handleClearStock = async (depotId, depotName) => {
    setClearingStock(depotId);
    try {
      const response = await axios.delete(`${API}/depots/${depotId}/stock`, { headers: getAuthHeaders() });
      toast.success(`${response.data.deleted_count} stock records cleared for ${depotName}`);
      fetchDepots();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clear stock data');
    } finally {
      setClearingStock(null);
    }
  };

  const handleClearSales = async (depotId, depotName) => {
    setClearingSales(depotId);
    try {
      const response = await axios.delete(`${API}/depots/${depotId}/sales`, { headers: getAuthHeaders() });
      toast.success(`${response.data.deleted_count} sales records cleared for ${depotName}`);
      fetchDepots();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clear sales data');
    } finally {
      setClearingSales(null);
    }
  };

  const handleClearTotalItems = async () => {
    setClearingTotals(true);
    try {
      const response = await axios.delete(`${API}/admin/clear-total-items`, { headers: getAuthHeaders() });
      toast.success(`Cleared ${response.data.total_deleted} "total" item records (Stock: ${response.data.stock_deleted}, Sales: ${response.data.sales_deleted})`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clear total items');
    } finally {
      setClearingTotals(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="depot-management-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Depot Management
          </h1>
          <p className="text-slate-500 mt-1">Manage regional depot locations</p>
        </div>
        
        <div className="flex gap-2">
          {/* Clear Total Items Button */}
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button 
                variant="outline" 
                className="text-amber-600 border-amber-200 hover:bg-amber-50"
                disabled={clearingTotals}
                data-testid="clear-total-items-btn"
              >
                {clearingTotals ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Clearing...
                  </>
                ) : (
                  <>
                    <AlertTriangle className="w-4 h-4 mr-2" />
                    Clear "Total" Items
                  </>
                )}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Clear All "Total" Item Records?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently delete all stock and sales records where the item name is "total" or "Total". 
                  These are typically junk rows from Excel uploads. This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction 
                  onClick={handleClearTotalItems}
                  className="bg-amber-600 hover:bg-amber-700"
                  data-testid="confirm-clear-totals"
                >
                  Clear Total Items
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-depot-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Depot
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create New Depot</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="depot-name">Depot Name</Label>
                  <Input
                    id="depot-name"
                    placeholder="e.g., Delhi"
                    value={newDepot.name}
                    onChange={(e) => setNewDepot(prev => ({ ...prev, name: e.target.value }))}
                    data-testid="depot-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="depot-location">Location / State</Label>
                  <Input
                    id="depot-location"
                    placeholder="e.g., Delhi NCR"
                    value={newDepot.location}
                    onChange={(e) => setNewDepot(prev => ({ ...prev, location: e.target.value }))}
                    data-testid="depot-location-input"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleCreateDepot} data-testid="create-depot-btn">Create Depot</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Depots Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-48 rounded-xl" />
          ))}
        </div>
      ) : depots.length === 0 ? (
        <Card>
          <CardContent className="text-center py-16">
            <Building2 className="w-20 h-20 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-700 text-xl font-medium">No Depots Configured</p>
            <p className="text-slate-500 mt-2">Click "Add Depot" to create your first depot</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {depots.map((depot) => (
            <Card key={depot.id} className="card-hover" data-testid={`depot-card-${depot.name}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-slate-900 rounded-xl flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{depot.name}</CardTitle>
                      <div className="flex items-center gap-1 text-slate-500 text-sm mt-1">
                        <MapPin className="w-3 h-3" />
                        {depot.location}
                      </div>
                    </div>
                  </div>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="icon" className="text-slate-400 hover:text-red-500">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete {depot.name} Depot?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This will permanently delete the depot and all associated users, stock, and sales data. This action cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction 
                          onClick={() => handleDeleteDepot(depot.id, depot.name)}
                          className="bg-red-600 hover:bg-red-700"
                          data-testid={`confirm-delete-${depot.name}`}
                        >
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Calendar className="w-4 h-4" />
                    Last Stock Upload
                  </div>
                  <Badge variant={depot.last_stock_upload ? 'default' : 'secondary'} className={depot.last_stock_upload ? 'bg-green-100 text-green-700' : ''}>
                    {depot.last_stock_upload 
                      ? new Date(depot.last_stock_upload).toLocaleDateString() 
                      : 'Never'}
                  </Badge>
                </div>
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Calendar className="w-4 h-4" />
                    Last Sales Upload
                  </div>
                  <Badge variant={depot.last_sales_upload ? 'default' : 'secondary'} className={depot.last_sales_upload ? 'bg-green-100 text-green-700' : ''}>
                    {depot.last_sales_upload 
                      ? new Date(depot.last_sales_upload).toLocaleDateString() 
                      : 'Never'}
                  </Badge>
                </div>
                
                {/* Clear Stock Data Button */}
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full mt-2 text-orange-600 border-orange-200 hover:bg-orange-50 hover:text-orange-700"
                      disabled={clearingStock === depot.id || !depot.last_stock_upload}
                      data-testid={`clear-stock-${depot.name}`}
                    >
                      {clearingStock === depot.id ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Clearing...
                        </>
                      ) : (
                        <>
                          <Package className="w-4 h-4 mr-2" />
                          Clear Stock Data
                        </>
                      )}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Clear Stock Data for {depot.name}?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This will permanently delete all stock records for <strong>{depot.name}</strong> depot. 
                        This action cannot be undone. Sales data and user accounts will not be affected.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction 
                        onClick={() => handleClearStock(depot.id, depot.name)}
                        className="bg-orange-600 hover:bg-orange-700"
                        data-testid={`confirm-clear-stock-${depot.name}`}
                      >
                        Clear Stock Data
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>

                {/* Clear Sales Data Button */}
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full mt-1 text-purple-600 border-purple-200 hover:bg-purple-50 hover:text-purple-700"
                      disabled={clearingSales === depot.id || !depot.last_sales_upload}
                      data-testid={`clear-sales-${depot.name}`}
                    >
                      {clearingSales === depot.id ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Clearing...
                        </>
                      ) : (
                        <>
                          <TrendingUp className="w-4 h-4 mr-2" />
                          Clear Sales Data
                        </>
                      )}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Clear Sales Data for {depot.name}?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This will permanently delete all sales records for <strong>{depot.name}</strong> depot. 
                        This action cannot be undone. Stock data and user accounts will not be affected.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction 
                        onClick={() => handleClearSales(depot.id, depot.name)}
                        className="bg-purple-600 hover:bg-purple-700"
                        data-testid={`confirm-clear-sales-${depot.name}`}
                      >
                        Clear Sales Data
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default DepotManagement;
